import { useEffect, useState, type FormEvent } from 'react';
import { useRegularizacion } from '../context/RegularizacionContext';
import { aporteCampanaService, type PagoCampana } from '../services/aporteCampanaService';
import type { Movimiento } from '../lib/supabase';
import type { EntidadDinero, Respaldo } from '../types/comprobante';
import { esResultadoCampanaIncierto } from '../utils/fondosCampanaRpc';
import { formatCurrency, formatDate } from '../utils/formatters';
import { AdjuntarComprobanteModal, type ItemParaAdjuntar } from './comprobantes/AdjuntarComprobanteModal';
import { ComprobantesDeMovimiento } from './comprobantes/ComprobantesDeMovimiento';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { Textarea } from './ui/Textarea';

interface Props {
  readonly aporteId: string;
  readonly readOnly: boolean;
  /** La entidad que guarda los comprobantes de los pagos. Sin ella (o en solo lectura) no se puede adjuntar desde aquí. */
  readonly entidad?: EntidadDinero | null;
  readonly onCambio: () => void;
  readonly onBloqueo: (bloqueado: boolean) => void;
}

export function HistorialPagosCampana({ aporteId, readOnly, entidad = null, onCambio, onBloqueo }: Props) {
  const { recargar: recargarRegularizacion } = useRegularizacion();
  const [ahora, setAhora] = useState(() => new Date());
  const [paraAdjuntar, setParaAdjuntar] = useState<{ items: readonly ItemParaAdjuntar[]; modo: 'adjuntar' | 'reemplazar' } | null>(null);
  const [pagos, setPagos] = useState<PagoCampana[]>([]);
  const [loading, setLoading] = useState(true);
  const [seleccionado, setSeleccionado] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [incierto, setIncierto] = useState(false);
  useEffect(() => {
    let vigente = true;
    aporteCampanaService.getPagos(aporteId).then(data => {
      if (vigente) setPagos(data);
    }).catch(() => {
      if (vigente) setError('No pudimos cargar el historial de pagos.');
    }).finally(() => { if (vigente) setLoading(false); });
    return () => { vigente = false; };
  }, [aporteId]);

  const abrirAdjuntar = (pago: Movimiento, respaldo: Respaldo, modo: 'adjuntar' | 'reemplazar') => {
    setParaAdjuntar({
      modo,
      items: [{ movimientoId: pago.id, respaldo, descripcion: pago.descripcion ?? null, monto: Number(pago.monto), fecha: pago.fecha }],
    });
  };

  const alAdjuntar = () => {
    aporteCampanaService.getPagos(aporteId)
      .then((data) => { setPagos(data); setAhora(new Date()); })
      .catch(() => { setError('El comprobante se guardó. No pudimos actualizar el historial.'); });
    recargarRegularizacion();
    onCambio();
  };

  const anular = async (event: FormEvent) => {
    event.preventDefault();
    if (!seleccionado || !motivo.trim() || readOnly) return;
    setSubmitting(true);
    onBloqueo(true);
    setError('');
    try {
      await aporteCampanaService.anularPago(seleccionado, motivo);
      setIncierto(false);
      onBloqueo(false);
      setSeleccionado(null);
      setMotivo('');
      onCambio();
      // El pago ya se anuló; un fallo posterior de lectura se informa aparte.
      aporteCampanaService.getPagos(aporteId).then(setPagos).catch(() => {
        setError('La anulación se confirmó. No pudimos actualizar el historial.');
      });
    } catch (error_: unknown) {
      const desconocido = esResultadoCampanaIncierto(error_);
      setIncierto(desconocido);
      onBloqueo(desconocido);
      const message = typeof error_ === 'object' && error_ !== null && 'message' in error_
        && typeof error_.message === 'string' ? error_.message : 'No pudimos anular el pago.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-6 border-t border-slate-200 pt-4">
      <h3 className="mb-3 font-semibold text-slate-800">Historial de pagos</h3>
      <FormErrorAlert error={error} />
      {loading && <output className="block">Cargando historial de pagos…</output>}
      {!loading && !error && pagos.length === 0 && <p className="text-sm text-slate-500">Sin pagos monetarios registrados.</p>}
      <ul className="space-y-3">
        {pagos.map(pago => (
          <li key={pago.id} className="rounded-lg border border-slate-200 p-3 text-sm">
            <p>{formatDate(pago.fecha)} · {formatCurrency(pago.monto)} · {pago.anulado ? 'Anulado' : 'Vigente'}</p>
            {!pago.anulado && (
              <div className="mt-2">
                <ComprobantesDeMovimiento movimiento={pago as unknown as Movimiento} ahora={ahora}
                  onAdjuntar={!readOnly && entidad ? abrirAdjuntar : undefined} />
              </div>
            )}
            {pago.anulado && <p className="mt-1 text-slate-600">{pago.motivo_anulacion} · {pago.fecha_anulacion}
              {pago.anulado_por_usuario_id && ` · Responsable: ${pago.anulado_por_usuario_id}`}</p>}
            {!readOnly && !pago.anulado && !seleccionado && <button type="button"
              onClick={() => { setSeleccionado(pago.id); setMotivo(''); }}
              className="mt-2 text-red-700 underline">Anular con motivo</button>}
          </li>
        ))}
      </ul>
      {seleccionado && <form onSubmit={anular} className="mt-4 space-y-3 rounded-lg bg-red-50 p-3">
        <p className="text-sm">Conservarás el historial. Si el pago sostiene un gasto o un envío aún no confirmado, regularízalo antes de anularlo. Si ya sostiene un envío confirmado, el pago no se puede anular.</p>
        <Textarea label="Motivo de anulación" value={motivo} onChange={event => setMotivo(event.target.value)} required disabled={submitting || incierto} />
        <div className="flex gap-3">
          <button type="button" disabled={submitting || incierto} onClick={() => setSeleccionado(null)}
            className="rounded-lg border px-3 py-2 disabled:opacity-50">Cancelar</button>
          <button type="submit" disabled={submitting || readOnly} className="rounded-lg bg-red-700 px-3 py-2 text-white disabled:opacity-50">
            {incierto ? 'Verificar anulación' : 'Confirmar anulación'}
          </button>
        </div>
      </form>}
      {paraAdjuntar && entidad && (
        <AdjuntarComprobanteModal isOpen entidad={entidad} items={paraAdjuntar.items} modo={paraAdjuntar.modo}
          onClose={() => { setParaAdjuntar(null); }} onSuccess={alAdjuntar} />
      )}
    </section>
  );
}
